/**
 * ImpactMapAnalysisPanel — Analysoi (PRD §4c).
 *
 * One button runs the AI's synthesis of the map as it stands; the result is a
 * row, so earlier runs stay listed and can be reopened. Nothing here writes
 * into the map. The compare view is the next step; this shows one result at a
 * time and the list.
 */
import React, { useCallback, useEffect, useState } from 'react';
import styled from '@emotion/styled';
import { X, Sparkles } from 'lucide-react';
import API_ENDPOINTS from '../../config/api';
import { useTranslation } from '../../hooks/useTranslation.jsx';

const DATE_LOCALE = { fi: 'fi-FI', en: 'en-GB', sv: 'sv-FI' };

const Panel = styled.aside`
  ${(p) => (p.$inline ? `
  position: relative; width: 100%; min-height: 0; flex: 1;
  background: none; border: none; border-radius: 0; box-shadow: none;
  ` : `
  position: absolute; right: 16px; top: 72px; bottom: 88px; width: min(520px, calc(100vw - 32px));
  background: rgba(14, 14, 18, 0.97); border: 1px solid rgba(255,255,255,0.12); border-radius: 12px;
  box-shadow: 0 12px 40px rgba(0,0,0,0.5); z-index: 40;
  `)}
  color: #eee; display: flex; flex-direction: column;
`;
const Head = styled.div`display:flex; align-items:center; gap:10px; padding:14px 16px; border-bottom:1px solid rgba(255,255,255,0.1);`;
const Title = styled.div`font-weight:700; font-size:1rem; flex:1;`;
const IconBtn = styled.button`background:none; border:none; color:#aaa; cursor:pointer; padding:4px; &:hover{color:#fff}`;
const Body = styled.div`overflow:auto; padding:${(p) => (p.$inline ? '0' : '14px 16px')}; display:flex; flex-direction:column; gap:14px; font-size:0.9rem; line-height:1.5;`;
const Run = styled.button`
  display:inline-flex; align-items:center; gap:8px; padding:10px 16px; border-radius:999px; border:1px solid #7c3aed;
  background: rgba(124,58,237,0.18); color:#e9d5ff; font:inherit; font-weight:700; cursor:pointer;
  &:disabled{opacity:.5; cursor:progress}
`;
const Label = styled.input`flex:1; min-width:0; padding:8px 10px; border-radius:8px; border:1px solid rgba(255,255,255,0.15); background:rgba(255,255,255,0.05); color:#eee; font:inherit;`;
const H = styled.div`font-size:0.72rem; font-weight:700; letter-spacing:0.08em; text-transform:uppercase; color:#9ca3af; margin-bottom:4px;`;
const Aloud = styled.blockquote`margin:0; padding:12px 14px; border-left:3px solid #7c3aed; background:rgba(124,58,237,0.08); border-radius:0 8px 8px 0; font-style:italic;`;
const Item = styled.div`padding:8px 0; border-top:1px solid rgba(255,255,255,0.06);`;
const Chip = styled.button`
  display:inline; padding:1px 7px; margin:0 1px; border-radius:999px; border:1px solid rgba(167,139,250,0.5);
  background:rgba(124,58,237,0.14); color:#e9d5ff; font:inherit; font-size:0.82em; cursor:pointer; vertical-align:baseline;
  &:hover{background:rgba(124,58,237,0.3)} &:disabled{opacity:.5; cursor:default}
`;
const Meta = styled.div`font-size:0.76rem; color:#9ca3af;`;
const Count = styled.button`
  display:inline; padding:0 2px; border:none; background:none; color:#c4b5fd; font:inherit; cursor:pointer; white-space:nowrap;
  &:hover{color:#e9d5ff; text-decoration:underline}
`;
const NodeList = styled.ul`
  list-style:none; margin:6px 0 2px; padding:0 0 0 10px; border-left:2px solid rgba(124,58,237,0.35);
  display:flex; flex-direction:column; gap:5px;
  li{line-height:1.3}
`;
const ChoiceTitle = styled.button`
  display:block; width:100%; text-align:left; background:none; border:none; padding:0; margin:0 0 3px;
  color:#a78bfa; font:inherit; font-weight:700; cursor:pointer; &:hover{text-decoration:underline}
`;
const ListBtn = styled.button`
  display:block; width:100%; text-align:left; padding:8px 10px; border-radius:8px; border:1px solid transparent; background:none; color:#ddd; font:inherit; cursor:pointer;
  &:hover{background:rgba(255,255,255,0.05)} &[aria-current="true"]{border-color:#7c3aed}
`;

// canRun: the Analysoi control, for the facilitator only (#1408); everyone sees the saved runs.
export default function ImpactMapAnalysisPanel({ instanceId, onClose, onFocusNodes, inline = false, canRun = false }) {
  const { t, language } = useTranslation();
  const ta = (key, vars) => t(`impactMapV2.analysis.${key}`, vars);
  const [list, setList] = useState([]);
  const [current, setCurrent] = useState(null);
  const [running, setRunning] = useState(false);
  const [label, setLabel] = useState('');
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    const res = await fetch(API_ENDPOINTS.IMPACT_MAP_ANALYSES(instanceId));
    const data = await res.json();
    setList(data.analyses || []);
    return data.analyses || [];
  }, [instanceId]);

  const open = useCallback(async (id) => {
    const res = await fetch(API_ENDPOINTS.IMPACT_MAP_ANALYSIS(instanceId, id));
    setCurrent(await res.json());
  }, [instanceId]);

  useEffect(() => { load().then((l) => { if (l[0]) open(l[0].id); }).catch((e) => setError({ key: 'loadFailed', message: e.message })); }, [load, open]);

  const run = async () => {
    setRunning(true); setError(null);
    try {
      const res = await fetch(API_ENDPOINTS.IMPACT_MAP_ANALYSES(instanceId), {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ label: label || null }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.details || data.error || `HTTP ${res.status}`);
      await load(); await open(data.id); setLabel('');
    } catch (e) { setError({ key: 'failed', message: e.message }); } finally { setRunning(false); }
  };

  const r = current?.result;
  const snap = current?.input_snapshot;

  // The prompt shows 8-char id prefixes and the model quotes them back in the
  // prose. Resolve each to the map: a node lights itself, a loop lights its
  // members, a blocker lights the consequence it blocks.
  const resolveRef = useCallback((prefix) => {
    if (!snap) return [];
    const n = (snap.nodes || []).find((x) => x.id.startsWith(prefix));
    if (n) return [n.id];
    const l = (snap.loops || []).find((x) => x.id.startsWith(prefix));
    if (l) return l.node_ids || [];
    const b = (snap.blockers || []).find((x) => x.id.startsWith(prefix));
    if (b) return [b.child_node_id || b.node_id].filter(Boolean);
    return [];
  }, [snap]);

  const labelFor = useCallback((prefix, max = 40) => {
    if (!snap) return prefix;
    const n = (snap.nodes || []).find((x) => x.id.startsWith(prefix));
    if (n) return (n.text || '').length > max ? `${(n.text || '').slice(0, max - 1)}…` : (n.text || '');
    const l = (snap.loops || []).find((x) => x.id.startsWith(prefix));
    if (l) return t('impactMapV2.analysis.refLoop', { title: (l.title || '').slice(0, 32) });
    const b = (snap.blockers || []).find((x) => x.id.startsWith(prefix));
    if (b) return t('impactMapV2.analysis.refBlocker', { text: (b.text || '').slice(0, 32) });
    return prefix;
  }, [snap, t]);

  // Prose with node references. The model quotes ids as [935de7a8] or bare in
  // parentheses. A single reference is a chip with the node's own words; a
  // run of them would swallow the sentence, so it reads as "(4 solmua)" and
  // opens into a list under the paragraph, one node per line, each a chip
  // that lights the map. A bare match needs a digit and must resolve, so a
  // word is never mistaken for one.
  const REF = /(\[[0-9a-f]{8}\]|\b(?=[0-9a-f]{0,7}\d)[0-9a-f]{8}\b)/g;
  // What a run of references is, in the map's own words: Seuraus and
  // Muuttuja are what the cards say, "kohta" only when a run mixes them.
  const kindOf = (prefix) => {
    const n = (snap?.nodes || []).find((x) => x.id.startsWith(prefix));
    if (!n) return 'other';
    // In the snapshot isScenario is is_scenario_starter: a variable, not
    // the root. Roots are depth 0.
    if (n.depth === 0) return 'scenario';
    return n.depth === 1 || n.isScenario ? 'variable' : 'consequence';
  };
  const countLabel = (refs) => {
    const kinds = new Set(refs.map(kindOf));
    const n = refs.length;
    if (kinds.size === 1 && kinds.has('consequence')) return ta('countConsequences', { count: n });
    if (kinds.size === 1 && kinds.has('variable')) return ta('countVariables', { count: n });
    return ta('countItems', { count: n });
  };
  const refOf = (part) => {
    const m = part.match(/^\[?([0-9a-f]{8})\]?$/);
    return m && resolveRef(m[1]).length ? m[1] : null;
  };
  const Prose = ({ text }) => {
    const [open, setOpen] = useState({});
    // Tokens; then runs of refs separated only by ", " / " ja " / "(" ")"
    // collapse into one group.
    const parts = String(text || '').split(REF);
    const items = [];
    for (let i = 0; i < parts.length; i += 1) {
      const ref = refOf(parts[i]);
      if (!ref) { items.push({ text: parts[i] }); continue; }
      const last = items[items.length - 1];
      const between = last && last.refs ? null : (items.length >= 2 && items[items.length - 2].refs ? last.text : null);
      if (between !== null && /^\s*(,|ja|sekä|and|och)?\s*$/.test(between)) {
        items.pop();
        items[items.length - 1].refs.push(ref);
      } else {
        items.push({ refs: [ref] });
      }
    }
    const groups = items.filter((it) => it.refs && it.refs.length > 1);
    return (
      <>
        <span>
          {items.map((it, i) => {
            if (!it.refs) {
              // The parentheses around a collapsed run are the count's own.
              const t = groups.length ? it.text.replace(/\(\s*$/, '').replace(/^\s*\)/, '') : it.text;
              return <React.Fragment key={i}>{t}</React.Fragment>;
            }
            if (it.refs.length === 1) {
              const r = it.refs[0];
              return <Chip key={i} type="button" title={labelFor(r)} onClick={() => onFocusNodes?.(resolveRef(r))}>{labelFor(r)}</Chip>;
            }
            const gi = groups.indexOf(it);
            return (
              <Count key={i} type="button" aria-expanded={!!open[gi]} onClick={() => setOpen((o) => ({ ...o, [gi]: !o[gi] }))}>
                ({countLabel(it.refs)}{open[gi] ? ' ▴' : ' ▾'})
              </Count>
            );
          })}
        </span>
        {groups.map((g, gi) => open[gi] && (
          <NodeList key={gi}>
            {g.refs.map((r) => (
              <li key={r}><Chip type="button" title={labelFor(r, 400)} onClick={() => onFocusNodes?.(resolveRef(r))}>{labelFor(r, 72)}</Chip></li>
            ))}
          </NodeList>
        ))}
      </>
    );
  };
  const when = (iso) => new Date(iso).toLocaleString(DATE_LOCALE[language] || 'fi-FI', { day: 'numeric', month: 'numeric', hour: '2-digit', minute: '2-digit' });

  return (
    <Panel $inline={inline}>
      {!inline && <Head><Sparkles size={18} color="#a78bfa" /><Title>{ta('title')}</Title><IconBtn onClick={onClose} title={t('common.close')}><X /></IconBtn></Head>}
      <Body $inline={inline}>
        {canRun && (
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <Label placeholder={ta('labelPlaceholder')} value={label} onChange={(e) => setLabel(e.target.value)} />
            <Run onClick={run} disabled={running}><Sparkles size={16} />{running ? ta('running') : ta('run')}</Run>
          </div>
        )}
        {error && <div style={{ color: '#f87171' }}>{ta(error.key, error)}</div>}
        {running && <Meta>{ta('runningHint')}</Meta>}

        {r && (
          <>
            <Meta>{current.label || ta('untitled')} · {when(current.created_at)} · {current.model} · {ta('promptVersion', { version: current.prompt_version })}</Meta>
            <Aloud><Prose text={r.readAloud} /></Aloud>
            <div><H>{ta('tendency')}</H><Prose text={r.tendency} /></div>
            <div><H>{ta('keyChoices')}</H>
              {(r.keyChoices || []).map((c) => (
                <Item key={c.nodeId}>
                  <ChoiceTitle type="button" onClick={() => onFocusNodes?.([c.nodeId])}>{c.title}</ChoiceTitle>
                  <div><Prose text={c.why} /></div>
                </Item>
              ))}
            </div>
            <div><H>{ta('loops')}</H><Prose text={r.loops} /></div>
            <div><H>{ta('blockers')}</H><Prose text={r.blockers} /></div>
            <div><H>{ta('attention')}</H>
              {(r.attention || []).map((a, i) => (
                <Item key={i}><button type="button" onClick={() => onFocusNodes?.(a.nodeIds)} style={{ background: 'none', border: 'none', color: '#ddd', font: 'inherit', textAlign: 'left', cursor: 'pointer', padding: 0 }}>{a.what}</button></Item>
              ))}
            </div>
          </>
        )}

        {list.length > 1 && (
          <div><H>{ta('earlier')}</H>
            {list.map((item) => (
              <ListBtn key={item.id} aria-current={current?.id === item.id} onClick={() => open(item.id)}>
                <div>{item.label || ta('untitled')}</div>
                <Meta>{when(item.created_at)} · {item.model} · {ta('listMeta', { nodes: item.node_count, choices: item.choice_count })}</Meta>
              </ListBtn>
            ))}
          </div>
        )}
      </Body>
    </Panel>
  );
}
